import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { trackButton, trackEvent } from '../../utils/analytics';
import usePageTitle from '../../hooks/usePageTitle';
import { ADMIN_REVIEW_PATH, getAdminReviewSelection } from '../../community/adminReviewSections';
import '../../css/AuthStack.css';

const Login = () => {
  usePageTitle('Login');
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const reviewSearch = typeof from === 'string' && from.startsWith(`${ADMIN_REVIEW_PATH}?`)
    ? from.slice(ADMIN_REVIEW_PATH.length) : null;
  const destination = typeof from === 'string' && (/^\/sightings\?sighting=[A-Za-z0-9_-]+$/.test(from)
    || (reviewSearch !== null && !from.includes('#') && getAdminReviewSelection(reviewSearch))) ? from : '/';
  const signInWithFacebook = useAuthStore((s) => s.signInWithFacebook);

  // new email/password handlers
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [facebookNeedsLink, setFacebookNeedsLink] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    trackButton('auth_login_email_attempt');
    try {
      const res = await useAuthStore.getState().signInEmail(email, password);
      if (res?.user) {
        trackEvent('auth_login_success', { method: 'email' });
        navigate(destination, { replace: true });
      }
    } catch (err) {
      console.error(err);
      const message = err?.message || 'Unable to login with email';
      setError(message);
      trackEvent('auth_login_error', {
        method: 'email',
        error: message.slice(0, 120),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    trackButton('auth_login_facebook_attempt');
    try {
      const result = await signInWithFacebook({
        email: email.trim(),
        password,
      });

      if (result?.success) {
        trackEvent('auth_login_success', {
          method: 'facebook',
          merged: Boolean(result.merged),
        });
        navigate(destination, { replace: true });
        return;
      }

      if (result?.email) {
        setEmail(result.email);
      }
      setFacebookNeedsLink(Boolean(result?.requiresExistingLogin));
      setError(result?.error || 'Unable to login with Facebook.');
      trackEvent('auth_login_error', {
        method: 'facebook',
        error: String(result?.error || 'Unknown Facebook login error').slice(0, 120),
      });
    } catch (err) {
      console.error(err);
      const message = err?.message || 'Unable to login with Facebook.';
      setError(message);
      trackEvent('auth_login_error', {
        method: 'facebook',
        error: message.slice(0, 120),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth__page">
      <div className="auth__container">
        <header className="auth__header">
          <h1 className="auth__heading">Login to your account</h1>
          <p className="auth__byline">
            Don&apos;t have an account yet?{' '}
            <Link to="/register" className="underline">
              Register.
            </Link>
          </p>
        </header>

        <form onSubmit={handleEmailLogin} className="auth__form" noValidate>
          <div className="auth__field">
            <label htmlFor="login-email" className="auth__label">
              Email
            </label>
            <input
              id="login-email"
              name="email"
              className="auth__input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="auth__field">
            <label htmlFor="login-password" className="auth__label">
              Password
            </label>
            <input
              id="login-password"
              name="password"
              className="auth__input"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          {error && <div className="auth__error">{error}</div>}

          <button type="submit" disabled={loading} className="auth__buttonNotHovered">
            {loading ? 'Logging in...' : 'Login with Email'}
          </button>
        </form>

        <div className="auth__divider">or</div>

        <button
          type="button"
          onClick={handleFacebookLogin}
          className="auth__buttonNotHovered"
          disabled={loading}
        >
          {loading
            ? 'Connecting Facebook...'
            : (facebookNeedsLink ? 'Link Facebook to Existing Account' : 'Login with Facebook')}
        </button>
      </div>
    </div>
  );
};

export default Login;
